import { LocaleAnchor } from '@/i18n/router';
import { Localize } from '@/i18n/Localize';
import { sharedPortuguese } from '@/i18n/shared';
import React from 'react';
import { Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SectionHeadingProps {
  /** The hash people can share, e.g. anchor="pricing" links to /gizmosql#pricing. */
  anchor: string;
  /** Heading level. Defaults to h2. */
  as?: 'h2' | 'h3';
  /** Classes for the heading element (keep the section's existing heading styles here). */
  className?: string;
  /** When true, render the `id` (and sticky-nav scroll offset) on the heading itself.
   *  Use this when the parent <section> does NOT already carry id={anchor}. Leave false
   *  (default) when the section owns the id, to avoid a duplicate id. */
  withId?: boolean;
  children: React.ReactNode;
}

/**
 * A section heading that doubles as a shareable deep-link. The heading text is wrapped
 * in an anchor to `#anchor`, revealing a small link icon on hover; clicking it sets the
 * URL hash so it can be copied straight from the address bar. The actual `id` lives on
 * the parent <section> so existing in-page scroll targets keep working.
 */
const SectionHeading = ({ anchor, as: Tag = 'h2', className, withId = false, children }: SectionHeadingProps) => {
  return <Localize messages={sharedPortuguese}>{(
    <Tag id={withId ? anchor : undefined} className={cn('group', withId && 'scroll-mt-24', className)}>
      <LocaleAnchor
        href={`#${anchor}`}
        className="inline-flex items-center gap-2 no-underline"
        title="Link to this section"
      >
        <span>{children}</span>
        <Link2
          aria-hidden="true"
          /* explicit color (not currentColor) so the icon stays visible even on
             gradient-clipped/transparent-text headings like the benchmark chart */
          className="h-[0.7em] w-[0.7em] flex-shrink-0 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-70"
        />
      </LocaleAnchor>
    </Tag>
  )}</Localize>;
};

export default SectionHeading;
